import React, { useEffect, useRef, useState } from 'react';
import { useIsMounted } from '@hooks/useIsMounted';
import { Box } from '@components/Box';
import { Columns } from '@components/Columns';
import { Flex } from '@components/Flex';
import { CTALink } from '@components/CTALink';
import {
  StyledModal,
  StyledInnerContainer,
  StyledColumns,
  StyledCloseButton,
  StyledCloseIcon,
  ECardImageContainer,
  ECardImageWrapper,
  ECardImage,
  ProductTypeMenu,
  ProductTypeMenuItem,
  StyledThemeJoykitIcon,
  StyledButton,
  TextColumnWrapper,
  RightFlex,
  CardTitleMobileWrapper,
  CardTitleDesktopWrapper,
  LeftFlex,
  StartWithMobileButtonWrapper,
  StartWithDesktopButtonWrapper,
  CloseButtonMobileWrapper,
  CloseButtonDesktopWrapper,
  VariantLayoutWrapper,
  StyledBorder,
  StyledBorderMobile,
  StyledVariantColumns,
  StyledVariantColumn,
  StyledStylesColumn,
  VariantImageWrapper,
  VariantImage,
  StyledPageIcon,
  StyledFrame,
  switchButtonWWWidth,
  switchButtonSTDWidth,
  switchButtonIWidth,
  switchButtonPWidth,
  StyledSelectDropdownMobile,
} from './GalleryECardModal.styles';
import {
  eCardDetailData,
  productTypes,
  apiProductTypes,
  eventProductTypes,
} from '@data-ts/components/GalleryComponents/GalleryPageContent';
import { Text } from '@components/typography';
import { getUseTemplateCtaId, getSignUpScenario } from '@utils/gallery.util';
import { getGalleryCreateWeddingUrl } from '@utils/urlHelpers.util';
import { DEFAULT_VENDOR } from '@components/GalleryComponents/GalleryComponents.constants';
import { capitalize } from '@utils/strings.util';
const analytics = require('../../../../static/js/joy');

export interface GalleryECardModalProps
  extends Readonly<{
    cardDetails: GalleryECard;
    isModalOpen: boolean;
    handleClose: () => void;
    productType: GalleryProductType;
    isWeddingDesignSlider?: boolean;
  }> {}

const DEFAULT_ASPECT_RATIO = 0.72;

const getAspectRatio = (imageEl: HTMLImageElement | undefined) => {
  let aspectRatio = DEFAULT_ASPECT_RATIO;
  if (imageEl) {
    aspectRatio = imageEl.naturalWidth / imageEl.naturalHeight;
  }
  return aspectRatio;
};

const { designs, saveTheDate, invitation, prints } = productTypes;

const single: string = 'single';
const multi: string = 'multi';

const getSelectedVariantData = (
  initialActiveVariantFilter: GalleryECard,
  initialActiveProductType: GalleryProductType,
  initialActiveLayoutFilter: string,
  cardDetails: GalleryECard
) => {
  const activeVariants = cardDetails.theme?.marketingData.filter(
    item =>
      item.variantThemeId === initialActiveVariantFilter.variantThemeId &&
      ((initialActiveProductType !== designs && item.variationType === apiProductTypes[initialActiveProductType]) ||
        (initialActiveProductType === designs &&
          ((initialActiveLayoutFilter === single && item.variationType === 'aloha') ||
            (initialActiveLayoutFilter === multi && item.variationType === 'brannan'))))
  );
  if (activeVariants?.[0]) {
    return activeVariants?.[0];
  }
  return cardDetails;
};

export const GalleryECardModal: React.FC<GalleryECardModalProps> = ({
  cardDetails,
  isModalOpen,
  handleClose,
  productType,
  isWeddingDesignSlider = false,
}) => {
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_ASPECT_RATIO);
  const [isLoaded, setLoaded] = useState(false);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [activeProductType, setActiveProductType] = useState<GalleryProductType>(productType);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useIsMounted();
  const signUpScenario = getSignUpScenario(productType);
  const isSmallImage = activeProductType === designs;
  const printDesign: GalleryECard | undefined = cardDetails.theme.marketingData.filter(
    marketingDataPrint => marketingDataPrint.variationType === 'prints'
  )[0];

  useEffect(() => {
    const imageEl = wrapperRef.current?.querySelector('img');
    if (imageEl?.complete) {
      onImageLoaded(imageEl);
    }
    if (productType) {
      setActiveProductType(productType);
    }
  }, [productType]);

  const onImageLoaded = (imageEl: HTMLImageElement) => {
    setAspectRatio(getAspectRatio(imageEl));
    setLoaded(true);
  };

  const variants = cardDetails.theme.marketingData.filter(item => item.variationType === 'thumbnail');
  const variant = cardDetails.theme.marketingData.filter(
    item =>
      item.variantThemeId === cardDetails.variantThemeId || (variants.length === 1 && item.theme.themeId === cardDetails.theme.themeId)
  )?.[0];
  const [isHoveredSingle, setHoveredSingle] = useState(false);
  const [isHoveredMulti, setHoveredMulti] = useState(false);
  const [activeLayoutFilter, setActiveLayoutFilter] = useState(cardDetails.variationType === 'brannan' ? multi : single);
  const [activeVariantFilter, setActiveVariantFilter] = useState<GalleryECard>(variant);
  const [selectedVariant, setSelectedVariant] = useState<GalleryECard>(cardDetails);
  const themeId = selectedVariant.variantThemeId ? selectedVariant.variantThemeId : selectedVariant.theme.themeId;

  useEffect(() => {
    setSelectedVariant(getSelectedVariantData(activeVariantFilter, activeProductType, activeLayoutFilter, cardDetails));
  }, [setSelectedVariant, activeVariantFilter, activeProductType, activeLayoutFilter, cardDetails]);

  let marginTop: string = '0px';
  let opacity: number = 0;
  if (containerRef.current) {
    const containerHeight: number = containerRef.current?.offsetHeight ? containerRef.current?.offsetHeight : 0;
    const windowHeight: number = typeof window === 'undefined' ? containerHeight : window.innerHeight ? window.innerHeight : 0;
    marginTop = windowHeight > containerHeight ? (windowHeight - containerHeight) / 2 + 'px' : '0px';
    opacity = 1;
  }

  return (
    <StyledModal
      isOpen={isModalOpen}
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false}
      style={{
        overlay: {
          background: 'rgba(0, 0, 0, 0.25)',
          zIndex: 101,
          overflow: 'hidden',
          overflowY: 'auto',
        },
        content: {
          opacity,
          marginTop: marginTop,
        },
      }}
    >
      <StyledInnerContainer ref={containerRef} alignItems="center" justifyContent="center">
        <StyledColumns centered>
          <Columns.Column width={8}>
            <LeftFlex flexDirection="column" alignItems="center">
              <CloseButtonMobileWrapper>
                <StyledCloseButton
                  id="couple-testimonial-close"
                  variant="altPurpleSecondary"
                  onClick={handleClose}
                  skipRedirectWithTelemetry={true}
                >
                  <StyledCloseIcon name="notAttending" color="mono1" />
                </StyledCloseButton>
              </CloseButtonMobileWrapper>
              <CardTitleMobileWrapper marginBottom={[6]} alignItems="center" flexDirection="column">
                <Text color="neutra4" variant="featureHed17" tagName="h1">
                  {productType === saveTheDate
                    ? eCardDetailData.freeOnlineSaveTheDate
                    : productType === designs
                    ? eCardDetailData.freeDesigns
                    : eCardDetailData.freeOnlineInvitation}
                </Text>
                <Text color="black" variant="hed40" tagName="h1">
                  {capitalize(selectedVariant.variantName ? selectedVariant.variantName : selectedVariant.theme.name)}
                </Text>
                <Text color="neutra6" variant="featureCopy17" tagName="h1">
                  {cardDetails.vendor == DEFAULT_VENDOR ? '' : cardDetails.vendor}
                </Text>
              </CardTitleMobileWrapper>
              <ECardImageContainer $isSmall={isSmallImage}>
                <ECardImageWrapper
                  ref={wrapperRef}
                  aspectRatio={aspectRatio}
                  $isSmall={isSmallImage}
                  showShadow={activeProductType !== prints}
                >
                  {activeProductType === designs && isLoaded ? (
                    <StyledFrame>
                      <svg width="36" height="8" viewBox="0 0 36 8">
                        <circle cx="4.0918" cy="3.99902" r="4" fill="#EBEBEB" />
                        <circle cx="17.7012" cy="3.99902" r="4" fill="#EBEBEB" />
                        <circle cx="31.3125" cy="3.99902" r="4" fill="#EBEBEB" />
                      </svg>
                    </StyledFrame>
                  ) : null}
                  <ECardImage
                    optimization="default"
                    src={selectedVariant.imageUrl + '&height=500'}
                    alt={`${selectedVariant.variantName ? selectedVariant.variantName : selectedVariant.theme.name}`}
                    $isLoaded={isLoaded}
                    $isSmall={isSmallImage}
                    $isDesigns={activeProductType === designs}
                    onLoad={({ currentTarget }) => {
                      onImageLoaded(currentTarget);
                    }}
                  />
                </ECardImageWrapper>
              </ECardImageContainer>
              <ProductTypeMenu
                key={`product-type-menu-${isMounted}`}
                isRightSelected={activeProductType === saveTheDate}
                isInvitationSelected={activeProductType === invitation}
                isPrintSelected={activeProductType === prints}
                hasPrint={printDesign ? true : false}
                isMounted={isMounted}
              >
                <ProductTypeMenuItem
                  id="website_menu_item"
                  $isActive={activeProductType === designs}
                  width={switchButtonWWWidth}
                  skipRedirectWithTelemetry
                  onClick={event => {
                    event.preventDefault();
                    setActiveProductType(designs);
                  }}
                >
                  {designs}
                </ProductTypeMenuItem>
                <ProductTypeMenuItem
                  id="save_the_date_menu_item"
                  $isActive={activeProductType === saveTheDate}
                  width={switchButtonSTDWidth}
                  skipRedirectWithTelemetry
                  onClick={event => {
                    event.preventDefault();
                    setActiveProductType(saveTheDate);
                  }}
                >
                  {saveTheDate}
                </ProductTypeMenuItem>
                <ProductTypeMenuItem
                  id="wedding_invitation_menu_item"
                  $isActive={activeProductType === invitation}
                  width={switchButtonIWidth}
                  skipRedirectWithTelemetry
                  onClick={event => {
                    event.preventDefault();
                    setActiveProductType(invitation);
                  }}
                >
                  {invitation}
                </ProductTypeMenuItem>
                {printDesign ? (
                  <ProductTypeMenuItem
                    id="wedding_prints_menu_item"
                    $isActive={activeProductType === prints}
                    width={switchButtonPWidth}
                    skipRedirectWithTelemetry
                    onClick={event => {
                      event.preventDefault();
                      setActiveProductType(prints);
                    }}
                  >
                    {prints}
                  </ProductTypeMenuItem>
                ) : null}
              </ProductTypeMenu>
              <StyledSelectDropdownMobile
                preSelectedOption={activeProductType}
                options={['Wedding Website', 'Invitation', 'Save the Date', 'Prints']}
                hasPrint={printDesign ? true : false}
                isOpen={isOpenFilter}
                handleOpen={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setIsOpenFilter(!isOpenFilter);
                }}
                onClickedOption={selectedFilter => {
                  setIsOpenFilter(!isOpenFilter);
                  setActiveProductType(selectedFilter);
                }}
              />
            </LeftFlex>
          </Columns.Column>
          <Columns.Column width={4}>
            <RightFlex flexDirection="column" paddingBottom={[11, 0]} isDesgin={activeProductType === designs}>
              <StartWithMobileButtonWrapper>
                <StyledButton
                  id={getUseTemplateCtaId(selectedVariant.variationType, themeId)}
                  variant="primary"
                  forwardedAs={'a'}
                  target="_blank"
                  telemetryLabel={themeId}
                  href={getGalleryCreateWeddingUrl(signUpScenario, selectedVariant.variationType, themeId, selectedVariant.font)}
                >
                  {productType === saveTheDate
                    ? eCardDetailData.startSaveTheDateButton
                    : productType === designs
                    ? eCardDetailData.startDesignsButton
                    : eCardDetailData.startInvitationButton}
                </StyledButton>
              </StartWithMobileButtonWrapper>
              <TextColumnWrapper flexDirection="column">
                <CardTitleDesktopWrapper flexDirection="column">
                  <Text color="neutra4" variant="featureHed17" tagName="h1">
                    {productType === saveTheDate
                      ? eCardDetailData.freeOnlineSaveTheDate
                      : productType === designs
                      ? eCardDetailData.freeDesigns
                      : eCardDetailData.freeOnlineInvitation}
                  </Text>
                  <Text color="black" variant="hed40" tagName="h1">
                    {capitalize(selectedVariant.variantName ? selectedVariant.variantName : selectedVariant.theme.name)}
                  </Text>
                  <Text color="neutra6" variant="featureCopy17" tagName="h1">
                    {cardDetails.vendor == DEFAULT_VENDOR ? '' : cardDetails.vendor}
                  </Text>
                </CardTitleDesktopWrapper>
                <Box marginTop={6}>
                  {productType === designs ? (
                    <>
                      <Box marginBottom={0}>
                        <Text tagName="span" color="neutra6" variant="featureHed17">
                          {eCardDetailData.layout}
                        </Text>
                      </Box>
                      <Box paddingTop={3} paddingBottom={0}>
                        <StyledVariantColumns fluid={false}>
                          <StyledVariantColumn width={'half'} mobile={'half'}>
                            <VariantLayoutWrapper
                              id="disgnModalSignlePage"
                              onClick={() => {
                                setActiveProductType(designs);
                                setActiveLayoutFilter(single);
                              }}
                              onMouseEnter={() => setHoveredSingle(true)}
                              onMouseLeave={() => setHoveredSingle(false)}
                              isSelected={activeLayoutFilter === single}
                              isHover={isHoveredSingle}
                              skipRedirectWithTelemetry
                            >
                              <StyledPageIcon
                                name="single"
                                color={activeLayoutFilter === single ? 'mono1' : isHoveredSingle ? 'linkDefault' : 'mono2'}
                              />
                              <Text
                                tagName="span"
                                color={activeLayoutFilter === single ? 'mono1' : isHoveredSingle ? 'linkDefault' : 'mono2'}
                                variant="featureCopy17"
                              >
                                {eCardDetailData.singlePage}
                              </Text>
                            </VariantLayoutWrapper>
                          </StyledVariantColumn>
                          <StyledVariantColumn width={'half'} mobile={'half'}>
                            <VariantLayoutWrapper
                              id="disgnModalMultiPage"
                              onClick={() => {
                                setActiveProductType(designs);
                                setActiveLayoutFilter(multi);
                              }}
                              onMouseEnter={() => setHoveredMulti(true)}
                              onMouseLeave={() => setHoveredMulti(false)}
                              isSelected={activeLayoutFilter === multi}
                              isHover={isHoveredMulti}
                              skipRedirectWithTelemetry
                            >
                              <StyledPageIcon
                                name="multi"
                                color={activeLayoutFilter === multi ? 'mono1' : isHoveredMulti ? 'linkDefault' : 'mono2'}
                              />
                              <Text
                                tagName="span"
                                color={activeLayoutFilter === multi ? 'mono1' : isHoveredMulti ? 'linkDefault' : 'mono2'}
                                variant="featureCopy17"
                              >
                                {eCardDetailData.multiPage}
                              </Text>
                            </VariantLayoutWrapper>
                          </StyledVariantColumn>
                        </StyledVariantColumns>
                      </Box>
                    </>
                  ) : null}
                  {variants.length > 1 ? (
                    <>
                      <Box marginTop={6}>
                        <Text tagName="span" color="neutra6" variant="featureHed17">
                          {eCardDetailData.themeVariants}
                        </Text>
                      </Box>
                      <Box paddingTop={3} paddingBottom={0}>
                        <StyledVariantColumns multiline>
                          {variants.map((item, index) => {
                            return (
                              <StyledStylesColumn key={index} width={'one-fifth'} mobile={'one-fifth'}>
                                <VariantImageWrapper
                                  onClick={() => {
                                    setActiveVariantFilter(item);
                                  }}
                                  isSelected={activeVariantFilter ? variants.indexOf(activeVariantFilter) === index : false}
                                >
                                  <VariantImage src={item.imageUrl} alt={item.theme.name} optimization="default" />
                                </VariantImageWrapper>
                              </StyledStylesColumn>
                            );
                          })}
                        </StyledVariantColumns>
                      </Box>
                    </>
                  ) : null}
                  <StyledBorderMobile />
                  {printDesign ? (
                    <Flex alignItems="center" marginBottom={0} marginTop={6}>
                      <StyledThemeJoykitIcon name="matchingPrints" color="warmGray1" />
                      <Text marginLeft={4} tagName="span" color="mono1" variant="featureCopy17">
                        {eCardDetailData.matchingPrints}
                      </Text>
                      <CTALink
                        id="designs_print_link"
                        href={printDesign.printUrl + '?utm_source=joy&utm_medium=link&utm_campaign=partnership'}
                      >
                        <Text marginLeft={2} tagName="span" color="linkDefault" variant="featureCopy17">
                          {printDesign.vendor}
                        </Text>
                      </CTALink>
                    </Flex>
                  ) : null}
                  <Flex alignItems="center" marginBottom={5} marginTop={printDesign ? 5 : 6}>
                    <StyledThemeJoykitIcon name="customFonts" color="warmGray1" />
                    <Text marginLeft={4} tagName="span" color="mono1" variant="featureCopy17">
                      {eCardDetailData.customizeFonts}
                    </Text>
                  </Flex>
                  <Flex alignItems="center" marginBottom={5}>
                    <StyledThemeJoykitIcon name="connectedGuestList" color="warmGray1" />
                    <Text marginLeft={4} tagName="span" color="mono1" variant="featureCopy17">
                      {eCardDetailData.poweredBy}
                    </Text>
                  </Flex>
                  <Flex alignItems="center" marginBottom={5}>
                    <StyledThemeJoykitIcon name="eCardRSVP" color="warmGray1" />
                    <Text marginLeft={4} tagName="span" color="mono1" variant="featureCopy17">
                      {eCardDetailData.rsvpIncludes}
                    </Text>
                  </Flex>
                  {productType === designs ? (
                    <Flex alignItems="center" marginBottom={5}>
                      <StyledThemeJoykitIcon name="phone" color="neutra6" />
                      <Text marginLeft={4} tagName="span" color="mono1" variant="featureCopy17">
                        {eCardDetailData.optimizedMobile}
                      </Text>
                    </Flex>
                  ) : null}
                  <StyledBorder />
                </Box>
                <StartWithDesktopButtonWrapper marginTop={8}>
                  <StyledButton
                    id={getUseTemplateCtaId(selectedVariant.variationType, themeId)}
                    variant="primary"
                    forwardedAs={'a'}
                    target="_blank"
                    telemetryLabel={themeId}
                    href={getGalleryCreateWeddingUrl(signUpScenario, selectedVariant.variationType, themeId, selectedVariant.font)}
                    onClick={() => {
                      analytics.segment.sendTelemetryFilterEvent('StartCreate', {
                        type: 'track',
                        category: `newmarketing.${eventProductTypes[productType]}`,
                        action: 'StartCreate',
                        label: { themeId },
                        extraInfo: {
                          activeFilterThemeType: selectedVariant.tags.toString(),
                          activeFilterColor: selectedVariant.colors.toString(),
                          activeFilterLayout: selectedVariant.variationType === 'aloha' ? 'Single Page' : 'Multi-Page',
                          url: getGalleryCreateWeddingUrl(signUpScenario, selectedVariant.variationType, themeId, selectedVariant.font),
                          template: selectedVariant.variationType,
                          style: themeId,
                          fontName: selectedVariant.font,
                        },
                      });
                    }}
                  >
                    {productType === saveTheDate
                      ? eCardDetailData.startSaveTheDateButton
                      : productType === designs
                      ? eCardDetailData.startDesignsButton
                      : eCardDetailData.startInvitationButton}
                  </StyledButton>
                </StartWithDesktopButtonWrapper>
                {isWeddingDesignSlider && (
                  <Box style={{ textAlign: 'center' }} marginTop={[7]}>
                    <CTALink id="exploreMoreDesigns" href="/designs">
                      Explore more designs
                    </CTALink>
                  </Box>
                )}
              </TextColumnWrapper>
            </RightFlex>
          </Columns.Column>
          <CloseButtonDesktopWrapper>
            <StyledCloseButton
              id="couple-testimonial-close"
              variant="altPurpleSecondary"
              onClick={handleClose}
              skipRedirectWithTelemetry={true}
            >
              <StyledCloseIcon name="notAttending" color="mono1" />
            </StyledCloseButton>
          </CloseButtonDesktopWrapper>
        </StyledColumns>
      </StyledInnerContainer>
    </StyledModal>
  );
};
